// Nine Lives '84 - sound.
//
// Everything is synthesised with the Web Audio API in the spirit of the
// original's PC speaker: square-wave blips, filtered noise thuds, and a
// looping square-wave tune. Nothing is sampled, so there are no assets to
// load. The context is created on the first user gesture, as browsers
// require.

import type { GameEvent } from '../sim/types';
import type { Tune } from './tune';

export interface TuneNote {
  /** Frequency in Hz, or 0 for a rest. */
  freq: number;
  /** Duration in seconds. */
  secs: number;
}

export class Audio {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private noise!: AudioBuffer;
  private tuneOsc: OscillatorNode | null = null;
  private tuneGain: GainNode | null = null;
  private bassOsc: OscillatorNode | null = null;
  private bassGain: GainNode | null = null;
  private tuneTimer = 0;
  private tuneNextStart = 0;
  /** The tune currently looping, so callers can ask for a change only when needed. */
  currentTune: Tune | null = null;
  private tuneLevel = 0.11;
  /** Music off keeps the effects. */
  music = true;
  enabled = true;
  /** 0 .. 1, applied on top of the on/off switch. */
  volume = 0.7;

  /** Create the context; call from a click or key handler. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const ctx = new AudioContext();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this.enabled ? this.volume * 0.7 : 0;
    this.master.connect(ctx.destination);

    // Two seconds of white noise for percussive sounds.
    const len = ctx.sampleRate * 2;
    this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  }

  get ready(): boolean {
    return this.ctx !== null;
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    this.applyGain();
    return this.enabled;
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.applyGain();
  }

  private applyGain(): void {
    if (this.master) {
      const g = this.enabled ? this.volume * 0.7 : 0;
      this.master.gain.setTargetAtTime(g, this.ctx!.currentTime, 0.02);
    }
  }

  /**
   * Loop a tune: a square-wave melody and, if it has one, a triangle bass
   * line. A different tune replaces the one playing.
   */
  playTune(tune: Tune, level = 0.11): void {
    if (!this.ctx || !tune.melody.length || !this.music) return;
    if (this.tuneOsc && this.currentTune === tune) return;
    if (this.tuneOsc) this.stopTune();
    this.currentTune = tune;
    this.tuneLevel = level;
    const ctx = this.ctx;
    const voice = (type: OscillatorType, cutoff: number): [OscillatorNode, GainNode] => {
      const osc = ctx.createOscillator();
      osc.type = type;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = cutoff;
      osc.connect(filter).connect(gain).connect(this.master);
      osc.start();
      return [osc, gain];
    };
    const [osc, gain] = voice('square', 2400);
    this.tuneOsc = osc;
    this.tuneGain = gain;
    if (tune.bass) {
      const [bosc, bgain] = voice('triangle', 900);
      this.bassOsc = bosc;
      this.bassGain = bgain;
    }
    this.tuneNextStart = 0;

    const lay = (o: OscillatorNode, g: GainNode, notes: TuneNote[], lvl: number, start: number): number => {
      let t = start;
      for (const n of notes) {
        if (n.freq > 0) {
          o.frequency.setValueAtTime(n.freq, t);
          g.gain.setValueAtTime(lvl, t);
          g.gain.setValueAtTime(0, t + n.secs - 0.03);
        } else {
          g.gain.setValueAtTime(0, t);
        }
        t += n.secs;
      }
      return t;
    };

    // Schedule a loop's worth of notes ahead, then re-arm before it ends.
    const schedule = (): void => {
      if (this.tuneOsc !== osc) return;
      const start = Math.max(ctx.currentTime + 0.05, this.tuneNextStart);
      const end = lay(osc, gain, tune.melody, this.tuneLevel, start);
      if (tune.bass && this.bassOsc && this.bassGain) {
        lay(this.bassOsc, this.bassGain, tune.bass, this.tuneLevel * 1.6, start);
      }
      this.tuneNextStart = end;
      this.tuneTimer = window.setTimeout(schedule, (end - ctx.currentTime - 0.5) * 1000);
    };
    schedule();
  }

  stopTune(): void {
    if (this.tuneTimer) {
      clearTimeout(this.tuneTimer);
      this.tuneTimer = 0;
    }
    if (this.ctx) {
      const now = this.ctx.currentTime;
      for (const [o, g] of [
        [this.tuneOsc, this.tuneGain],
        [this.bassOsc, this.bassGain],
      ] as [OscillatorNode | null, GainNode | null][]) {
        if (!o || !g) continue;
        g.gain.cancelScheduledValues(now);
        g.gain.setValueAtTime(0, now);
        o.stop(now + 0.05);
      }
    }
    this.tuneOsc = null;
    this.tuneGain = null;
    this.bassOsc = null;
    this.bassGain = null;
    this.currentTune = null;
  }

  setMusic(on: boolean): void {
    this.music = on;
    if (!on) this.stopTune();
  }

  get tunePlaying(): boolean {
    return this.tuneOsc !== null;
  }

  /** A short filtered-noise burst: thuds, whooshes, splashes. */
  private burst(
    duration: number,
    gain: number,
    filterType: BiquadFilterType,
    freq: number,
    q = 1,
    freqEnd?: number,
  ): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = filterType;
    f.frequency.value = freq;
    f.Q.value = q;
    if (freqEnd !== undefined) {
      f.frequency.exponentialRampToValueAtTime(freqEnd, ctx.currentTime + duration);
    }
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    src.connect(f).connect(g).connect(this.master);
    src.start();
    src.stop(ctx.currentTime + duration);
  }

  /** A square-wave blip sliding from `from` to `to` Hz. */
  private blip(from: number, to: number, duration: number, gain = 0.15): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(from, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(to, ctx.currentTime + duration);
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(g).connect(this.master);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  }

  jump(): void {
    this.blip(330, 880, 0.12);
  }

  land(): void {
    this.burst(0.08, 0.35, 'lowpass', 300, 1, 80);
  }

  knocked(): void {
    this.burst(0.18, 0.6, 'bandpass', 500, 1.5, 120);
    this.blip(600, 150, 0.2, 0.1);
  }

  /** The dog: a low sawtooth snarl under a burst of noise. */
  growl(): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(110, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(70, ctx.currentTime + 0.6);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 28;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 30;
    lfo.connect(lfoGain).connect(osc.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.25, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
    osc.connect(g).connect(this.master);
    osc.start();
    lfo.start();
    osc.stop(ctx.currentTime + 0.7);
    lfo.stop(ctx.currentTime + 0.7);
    this.burst(0.5, 0.5, 'lowpass', 700, 0.8, 150);
  }

  /** A warning growl: shorter and quieter than the bite. */
  growlShort(): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(90, ctx.currentTime);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 22;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 25;
    lfo.connect(lfoGain).connect(osc.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.12, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.connect(g).connect(this.master);
    osc.start();
    lfo.start();
    osc.stop(ctx.currentTime + 0.35);
    lfo.stop(ctx.currentTime + 0.35);
  }

  /** An electric buzz for the eel. */
  buzz(): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = 55;
    const lfo = ctx.createOscillator();
    lfo.type = 'square';
    lfo.frequency.value = 40;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 40;
    lfo.connect(lfoGain).connect(osc.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.18, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
    osc.connect(g).connect(this.master);
    osc.start();
    lfo.start();
    osc.stop(ctx.currentTime + 0.45);
    lfo.stop(ctx.currentTime + 0.45);
  }

  /** The kiss: a smooch and a proper little tune. */
  kiss(): void {
    const ctx = this.ctx!;
    this.blip(1200, 400, 0.18, 0.15);
    const notes = [659, 784, 988, 1319, 988, 1319, 1568];
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = f;
      const g = ctx.createGain();
      const t = ctx.currentTime + 0.25 + i * 0.12;
      g.gain.setValueAtTime(0, t);
      g.gain.setValueAtTime(0.12, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + (i === notes.length - 1 ? 0.5 : 0.2));
      osc.connect(g).connect(this.master);
      osc.start(t);
      osc.stop(t + 0.55);
    });
  }

  /** A little rising arpeggio for a cleared room. */
  fanfare(): void {
    const ctx = this.ctx!;
    [523, 659, 784, 1047].forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = f;
      const g = ctx.createGain();
      const t = ctx.currentTime + i * 0.09;
      g.gain.setValueAtTime(0, t);
      g.gain.setValueAtTime(0.12, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.16);
      osc.connect(g).connect(this.master);
      osc.start(t);
      osc.stop(t + 0.18);
    });
  }

  /** Turn simulation events into sounds. */
  onEvent(ev: GameEvent): void {
    if (!this.ctx) return;
    switch (ev.kind) {
      case 'jump':
        this.jump();
        break;
      case 'land':
        this.land();
        break;
      case 'knocked':
        if (ev.by === 'broom') this.burst(0.3, 0.6, 'bandpass', 1200, 0.8, 300);
        this.knocked();
        break;
      case 'caught':
        this.growl();
        break;
      case 'respawn':
        this.blip(220, 660, 0.25, 0.1);
        break;
      case 'windowOpen':
        this.burst(0.05, 0.25, 'highpass', 3000, 1);
        break;
      case 'enterWindow':
        this.blip(440, 1320, 0.25, 0.12);
        break;
      case 'roomDone':
        this.fanfare();
        break;
      case 'roomStart':
        this.blip(660, 330, 0.2, 0.08);
        break;
      case 'pawprint':
        this.burst(0.03, 0.12, 'bandpass', 900, 2);
        break;
      case 'sweep':
        this.burst(0.25, 0.3, 'highpass', 2200, 0.7, 4000);
        break;
      case 'mouseCaught':
        this.blip(1200, 1800, 0.1, 0.12);
        this.blip(800, 500, 0.15, 0.08);
        break;
      case 'crawl':
        this.burst(0.35, 0.3, 'lowpass', 500, 1, 200);
        break;
      case 'fernDown':
        this.burst(0.2, 0.5, 'bandpass', 1500, 3, 400);
        break;
      case 'shooed':
        this.blip(700, 200, 0.4, 0.12);
        break;
      case 'nudge':
        this.burst(0.12, 0.5, 'bandpass', 700, 2, 300);
        break;
      case 'cageBroke':
        this.burst(0.5, 0.8, 'highpass', 1800, 0.6, 600);
        this.blip(1500, 400, 0.4, 0.08);
        break;
      case 'birdCaught':
        this.blip(1800, 2600, 0.08, 0.1);
        this.blip(2200, 3000, 0.08, 0.08);
        this.fanfare();
        break;
      case 'growl':
        this.growlShort();
        break;
      case 'dogWoke':
        this.growl();
        this.blip(300, 600, 0.12, 0.15);
        break;
      case 'drink':
        this.blip(900, 1100, 0.04, 0.05);
        break;
      case 'bowlEmpty':
        this.blip(600, 1200, 0.15, 0.1);
        break;
      case 'refill':
        this.blip(400, 250, 0.2, 0.08);
        this.blip(380, 240, 0.2, 0.08);
        break;
      case 'dive':
        this.burst(0.5, 0.7, 'lowpass', 1200, 0.5, 300);
        break;
      case 'surface':
        this.burst(0.25, 0.4, 'highpass', 1500, 0.7);
        break;
      case 'climbOut':
        this.blip(500, 800, 0.15, 0.08);
        break;
      case 'fishEaten':
        this.blip(1000, 1500, 0.08, 0.12);
        break;
      case 'shock':
        this.buzz();
        break;
      case 'drowned':
        this.burst(0.8, 0.5, 'lowpass', 600, 0.5, 100);
        this.blip(300, 80, 0.8, 0.12);
        break;
      case 'felicia':
        this.blip(880, 1320, 0.12, 0.1);
        this.blip(1320, 1760, 0.12, 0.1);
        break;
      case 'heartBroken':
        this.burst(0.15, 0.5, 'highpass', 2500, 1);
        this.blip(500, 250, 0.15, 0.08);
        break;
      case 'heartMended':
        this.blip(600, 1200, 0.15, 0.08);
        break;
      case 'gift':
        this.blip(1000, 1500, 0.08, 0.1);
        this.blip(1500, 2000, 0.08, 0.1);
        break;
      case 'bribe':
        this.blip(700, 350, 0.25, 0.1);
        break;
      case 'fallOut':
        this.blip(600, 150, 0.6, 0.12);
        break;
      case 'kiss':
        this.kiss();
        break;
      case 'leftRoom':
        this.blip(500, 300, 0.2, 0.08);
        break;
      case 'gameOver':
        this.blip(440, 110, 0.6, 0.12);
        break;
    }
  }
}
